/* =========================================================
 * GLOBAL TOAST KONTEXT – v2.1  (tek dosya, App.js DEĞİŞMEZ)
 *    › <ToastProvider> … </ToastProvider>
 *    › const toast = useToast();
 *      toast.info   ('Bilgi');
 *      toast.success('Başarılı');
 *      toast.error  ('Hata');
 *      // İsterseniz:
 *      toast.warn   ('Uyarı',   { duration: 6000 });
 *      toast.custom ('Özel!',   { type:'success', icon:'⭐' });
 * ========================================================= */
import React, {
    createContext, useCallback, useContext,
    useEffect, useMemo, useRef, useState,
  } from 'react';
  
  /* ---------- İç sabitler ---------- */
  const COLORS = {
    info   : '#3498db',
    success: '#2ecc71',
    error  : '#e74c3c',
    warn   : '#f39c12',
  };
  const DEF = {
    duration      : 3500,          // ms
    position      : 'top-right',   // tr|tl|br|bl
    dismissable   : true,
    pauseOnHover  : true,
    icon          : null,          // JSX | string
    maxVisible    : 5,
  };
  
  /* ---------- Tekil toast kartı ---------- */
  function ToastCard({
    id, msg, type, icon,
    duration, dismissable, pauseOnHover,
    onClose,
  }) {
    const timer = useRef(null);
  
    /* Yaşam süresi */
    useEffect(() => {
      timer.current = setTimeout(onClose, duration);
      return () => clearTimeout(timer.current);
    }, [duration, onClose]);
  
    const hoverProps = pauseOnHover
      ? {
          onMouseEnter: () => clearTimeout(timer.current),
          onMouseLeave: () =>
            (timer.current = setTimeout(onClose, duration)),
        }
      : {};
  
    return (
      <div
        className="toast"
        style={{ borderLeft: `6px solid ${COLORS[type] || COLORS.info}` }}
        role="status"
        aria-live="polite"
        {...hoverProps}
      >
        {icon && <span style={{ marginRight: 8 }}>{icon}</span>}
        <span style={{ flex: 1 }}>{msg}</span>
        {dismissable && (
          <button
            aria-label="Kapat"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: 16,
              lineHeight: 1,
              color: '#888',
            }}
          >
            ×
          </button>
        )}
      </div>
    );
  }
  
  /* ---------- Kontext ---------- */
  const ToastCtx = createContext(null);
  
  export const ToastProvider = ({
    children,
    options = {}, // İsteyen <ToastProvider options={{position:'br'}} />
  }) => {
    /* Varsayılanları birleştir */
    const cfg = useMemo(() => ({ ...DEF, ...options }), [options]);
  
    const [stack, setStack] = useState([]);
  
    /* Yeni toast ekle */
    const push = useCallback(
      (msg, type = 'info', opt = {}) => {
        const id = Date.now() + Math.random();
        const item = {
          id,
          msg,
          type,
          ...cfg,
          ...opt, // isteğe bağlı override
        };
        setStack((s) =>
          [...s, item].slice(-item.maxVisible)
        );
      },
      [cfg]
    );
  
    /* Kısayollar – Geriden uyumlu */
    const api = useMemo(
      () => ({
        info: (m, o) => push(m, 'info', o),
        success: (m, o) => push(m, 'success', o),
        error: (m, o) => push(m, 'error', o),
        warn: (m, o) => push(m, 'warn', o),
        custom: (m, o) => push(m, o?.type || 'info', o),
      }),
      [push]
    );
  
    /* Kapat */
    const remove = (id) =>
      setStack((s) => s.filter((t) => t.id !== id));
  
    /* Konum sınıfı */
    const posClass = {
      'top-right': 'tr',
      'top-left': 'tl',
      'bottom-right': 'br',
      'bottom-left': 'bl',
    }[cfg.position] || 'tr';
  
    return (
      <ToastCtx.Provider value={api}>
        {children}
        <div className={`toast-container ${posClass}`}>
          {stack.map((t) => (
            <ToastCard
              key={t.id}
              {...t}
              onClose={() => remove(t.id)}
            />
          ))}
        </div>
      </ToastCtx.Provider>
    );
  };
  
  export const useToast = () => useContext(ToastCtx);
  